export enum ErrorCode {
  LoginInvalidCredentials = "loginInvalidCredentials",
  LoginInvalidCredentialsTotp = "loginInvalidCredentialsTotp",
  LoginInactiveUser = "LoginInactiveUser",
  PatientAccountInvalidCredentials = "PatientAccountInvalidCredentials",
  PatientAccountInvalidCredentialsMfa = "PatientAccountInvalidCredentialsMfa",
  PatientAccountEmailAlreadyInUse = "PatientAccountEmailAlreadyInUse",
  PatientAccountLoginAttemptsExceeded = "PatientAccountLoginAttemptsExceeded",
  PatientAccountPasswordResetExpired = "PatientAccountPasswordResetExpired",
  PatientAccountLoginExpired = "PatientAccountLoginExpired",
  PatientAccountSmsAttemptsExceeded = "PatientAccountSmsAttemptsExceeded",
  PatientAccountSmsIncorrectCode = "PatientAccountSmsIncorrectCode",
  PatientAccountDuplicateEmail = "PatientAccountDuplicateEmail",
  // The HIS accepted the credentials but could not send the verification SMS.
  PatientAccountMfaSmsFailed = "PatientAccountMfaSmsFailed",
  RegistrationClosed = "RegistrationClosed",
  RegistrationAlreadyCreated = "RegistrationAlreadyCreated",
  RegistrationRequestExistingPatient = "RegistrationRequestExistingPatient",
  // Staff-created registration: the matched person already has an active
  // (Undecided) registration request or an active patient in this practice.
  RegistrationRequestDuplicateActive = "RegistrationRequestDuplicateActive",
  RegistrationRequestAcceptedHisKnown = "RegistrationRequestAcceptedHisKnown",
  RegistrationRequestCompletedHisUnknown = "RegistrationRequestCompletedHisUnknown",
  ReregistrationHisUnknown = "ReregistrationHisUnknown",
  RegistrationHisFailed = "RegistrationHisFailed",
  // The practice's HIS can receive registrations through neither the direct API
  // nor AutoMedic, so there is nothing to (re-)send it to.
  RegistrationHisCannotReceive = "RegistrationHisCannotReceive",
  // A send may have reached the HIS without confirming it (its connection dropped,
  // its worker stopped, or it is still waiting on the HIS), so sending again could
  // register the patient twice. Staff check the HIS before re-sending.
  RegistrationHisSendUnconfirmed = "RegistrationHisSendUnconfirmed",
  AppointmentCreationClosed = "AppointmentCreationClosed",
  // Expected outcome of a booking race: the slot was taken (or expired) between
  // the patient loading the slot list and submitting the booking.
  AppointmentSlotUnavailable = "AppointmentSlotUnavailable",
  // A practice books an appointment in the HIS for a patient whose file holds
  // no HIS patient id (`Patient.hisPatientId`), so the HIS has nobody to book
  // it for (IT-5114: HealthConnected patients registered through RPA).
  PatientNotLinkedToHis = "PatientNotLinkedToHis",
  WalkInConsultCreationClosed = "WalkInConsultCreationClosed",
  WalkInConsultCreationQueueFull = "WalkInConsultCreationQueueFull",
  IdentificationCheckError = "IdentificationCheckError",
  NoAgendaConfigured = "NoAgendaConfigured",
  HISLogout = "HISLogout",
  DuplicatePassword = "DuplicatePassword",
  PatientBSNAlreadyExists = "PatientBSNAlreadyExists",
  PatientBSNInvalid = "PatientBSNInvalid",
  CommunicationLogAccessDenied = "CommunicationLogAccessDenied",
  // Expected when a (time-limited) practice authorization expired or was
  // revoked while the frontend was still open — suppressed from warn-logs
  // and Sentry in `AllExceptionsFilter`.
  PracticeConfigurationAccessDenied = "PracticeConfigurationAccessDenied",
  UsernameAlreadyInUse = "UsernameAlreadyInUse",

  MfaSessionNotFound = "mfaSessionNotFound",
  MfaInvalidToken = "mfaInvalidToken",
  MfaSessionExpired = "mfaSessionExpired",
  MfaMaxAttemptsReached = "mfaMaxAttemptsReached",
  MfaContextMismatch = "mfaContextMismatch",
  MfaCallNotAllowed = "mfaCallNotAllowed",
  MfaDeliveryCooldown = "mfaDeliveryCooldown",
  MfaDeliveryFailed = "mfaDeliveryFailed",

  // E-consult public access errors
  EconsultPatientMissingPhoneNumber = "EconsultPatientMissingPhoneNumber",

  // HIS e-consult API errors
  HisEconsultsUnavailable = "HisEconsultsUnavailable",
  EconsultsInHisNotSupported = "EconsultsInHisNotSupported",
  EconsultsInHisRequiresDigiD = "EconsultsInHisRequiresDigiD",
  EconsultsInHisConnectionFailed = "EconsultsInHisConnectionFailed",
  EconsultsInHisDefaultRecipientRequired = "EconsultsInHisDefaultRecipientRequired",

  // Page invitation errors
  InvitationRecipientMismatch = "InvitationRecipientMismatch",
  InvitationNotRedeemable = "InvitationNotRedeemable",

  // User invitation errors
  DuplicateUserInvitation = "DuplicateUserInvitation",
  InvitationAccountAlreadyExists = "InvitationAccountAlreadyExists",
  EmailDomainBlocked = "EmailDomainBlocked",
  EmailNotDeliverable = "EmailNotDeliverable",

  // File import errors
  UnsupportedImportFile = "UnsupportedImportFile",

  // File upload errors
  FileContentTypeNotAllowed = "FileContentTypeNotAllowed",
  FileTooLarge = "FileTooLarge",
  ImageDimensionsTooLarge = "ImageDimensionsTooLarge",
  ImageProcessingFailed = "ImageProcessingFailed",

  // Postal code area errors
  PostalCodeP4DoesNotExist = "PostalCodeP4DoesNotExist",

  // UZI certificate upload errors (IT-4938). Surfaced inline under the file
  // pickers by UziCertificateSection, not toasted by the generic interceptor.
  UziCertificatePublicUnreadable = "UziCertificatePublicUnreadable",
  UziCertificateIssuerNotAccepted = "UziCertificateIssuerNotAccepted",
  UziCertificatePrivateUnreadable = "UziCertificatePrivateUnreadable",
  UziCertificateKeyPairMismatch = "UziCertificateKeyPairMismatch",

  // A civil registry (SBV-Z) check was asked for but cannot be made (IT-4637),
  // so SBV-Z was not asked: the practice has the checks off, or has no UZI
  // certificate SBV-Z would accept. Each its own code so the portal can tell
  // the practice what to fix.
  CivilRegistryCheckDisabled = "CivilRegistryCheckDisabled",
  CivilRegistryCheckNoUsableUziCertificate = "CivilRegistryCheckNoUsableUziCertificate",

  // Practice Vecozo permission probes on settings save (IT-4944). Toasted by
  // the registration-settings form itself, not the generic interceptor.
  VecozoCovPermissionDenied = "VecozoCovPermissionDenied",
  VecozoIonPermissionDenied = "VecozoIonPermissionDenied",

  // Vecozo did not answer the probe, so permission is unknown. Toasted by the
  // registration-settings form alongside the two denials above.
  VecozoUnavailable = "VecozoUnavailable",

  // The ION call of an accept failed without an ION result code. The accept is
  // rolled back and the registration request page offers a retry inline.
  VecozoIonRegistrationFailed = "VecozoIonRegistrationFailed",

  // A registration AGB code that is not a practice AGB code in the Vektis
  // registry (IT-4878). Toasted by the registration-settings form.
  PracticeRegistrationAgbUnknown = "PracticeRegistrationAgbUnknown",

  // A maintenance banner scoped to a practice that does not exist (IT-5091).
  // The admin picker submits ids from a list it fetched earlier, so this means
  // that list went stale and the form should re-fetch it.
  MaintenanceBannerPracticeUnknown = "MaintenanceBannerPracticeUnknown",

  // Anonymous e-consult link errors (phone UI)
  EconsultLinkCallerNumberInvalid = "EconsultLinkCallerNumberInvalid",
  EconsultLinkCallerNumberNotMobile = "EconsultLinkCallerNumberNotMobile",
  EconsultLinkTemplateDisabled = "EconsultLinkTemplateDisabled",
  // The SMS provider refused/failed the send; the e-consult creation was rolled
  // back with it, so pressing the send button again retries from scratch.
  EconsultLinkSmsFailed = "EconsultLinkSmsFailed",

  // Care group dashboard errors
  // The global build lock is held (by the recurring job or another admin), so
  // the on-demand rebuild did not run. The dashboard shell toasts a "try again
  // in a few minutes" message on this code instead of the generic error.
  DashboardDatabaseBuildRunning = "DashboardDatabaseBuildRunning",
  // No snapshot exists for this care group yet — the scheduled build has not run
  // for it. Expected on the dashboard's pre-flight, which renders an empty state
  // on this code instead of opening a database (and suppresses the toast).
  DashboardDatabaseNotBuilt = "DashboardDatabaseNotBuilt",

  // Patient swap list: the selection handed to the send endpoint holds nobody
  // with an email address, so there is no send that could succeed. The email
  // page normally prevents this, so it is the guard behind that, not the
  // primary signal.
  SwapListNoRecipientsWithEmail = "SwapListNoRecipientsWithEmail",

  // The practice is not assigned to a telephony tenant, so there is no Elevate
  // account to link a device to. Only an admin can fix it, under Phone > General.
  PhonePracticeHasNoTenant = "PhonePracticeHasNoTenant",
  // Telephony tenant administration: refusals the tenant form renders inline.
  // A name is unique across tenants, so the admin has to pick another one.
  PhoneTenantNameTaken = "PhoneTenantNameTaken",
  // The default tenant is where webhook deliveries without a `tenant` parameter
  // land, so it may only be deleted when it is the last tenant left.
  PhoneTenantIsDefault = "PhoneTenantIsDefault",
  // Practices are still assigned to this tenant; deleting it would leave their
  // devices linked to an account we no longer hold credentials for.
  PhoneTenantHasPractices = "PhoneTenantHasPractices",
  // Elevate refused the OAuth (device-linking) pair. Carries Elevate's own reason as
  // `detail`, since "invalid_client" tells an admin what to fix where a generic
  // "validation failed" does not.
  PhoneTenantOauthCredentialsRejected = "PhoneTenantOauthCredentialsRejected",
  // As above, for the client-credentials (notifications API) pair.
  PhoneTenantServiceCredentialsRejected = "PhoneTenantServiceCredentialsRejected",
  // Elevate could not be reached to check a credential pair. A pair that could not be
  // checked is refused rather than stored as if it had been.
  PhoneTenantCredentialsUnverifiable = "PhoneTenantCredentialsUnverifiable",
  // One stored secret of a pair no longer decrypts (the ENCRYPTION_KEY changed, or the row
  // came from another environment's data), so a change to the other half cannot be checked
  // or kept. Entering both halves again stores a fresh pair without reading the old one.
  PhoneTenantStoredSecretUnreadable = "PhoneTenantStoredSecretUnreadable",
  // Registering the tenant's webhook subscription with Elevate failed, on the admin's
  // "register webhook again". Carries Elevate's own reason as `detail`, which for an
  // `invalid_client` points at the service credentials.
  PhoneTenantWebhookRegistrationFailed = "PhoneTenantWebhookRegistrationFailed",

  // Patient swap upload: the file was already imported into another practice
  // than the one the process call names. A file is imported once, and its
  // receipt only answers a repeat for the practice it was imported into.
  PatientSwapFileImportedElsewhere = "PatientSwapFileImportedElsewhere",

  // Civil registry (SBV-Z) check of a registration, asked for by hand again
  // too soon after the last time.
  CivilRegistryRecheckCooldown = "CivilRegistryRecheckCooldown",

  // Duplicate patient files (IT-5070). Every rule the link/unlink service
  // enforces on top of the schema has its own code, so the link dialog can name
  // the rule that was hit instead of a generic "that did not work".
  // See `backend/src/patients/README.md` § Patient file relations.
  PatientFileLinkSelf = "PatientFileLinkSelf",
  PatientFileLinkOtherPractice = "PatientFileLinkOtherPractice",
  PatientFileLinkMainIsDuplicate = "PatientFileLinkMainIsDuplicate",
  PatientFileLinkMainNotActive = "PatientFileLinkMainNotActive",
  PatientFileLinkAlreadyLinked = "PatientFileLinkAlreadyLinked",
  PatientFileLinkNotLinked = "PatientFileLinkNotLinked",

  // Plato errors we can handle (see `PlatoErrorType`)
  NOT_CURRENT_QUESTION = "NOT_CURRENT_QUESTION",
}
